import React, { useState } from "react";
import {
    Hotel,
    HotelSearchFilters,
    HotelListRequest,
} from "../Types/hotelTypes";
import DataTable, { ColumnConfig } from "../../Shared/Components/DataTable";
import HotelSearchForm from "../Components/HotelSearchForm";
import { useGetHotelsListQuery } from "../Service/extendedHotelsApi";
import { useDeleteHotelMutation } from "../Service/hotelsApi";
import { useNavigate } from "react-router-dom";
import ActionButtons from "../../Shared/Components/ActionButtons";
import { useCreateRedirect } from '../../Shared/Hooks/useCreateRedirect';
import { useDeleteSelectedItems } from '../../Shared/Hooks/useDeleteSelectedItems';
import { HotelsRoutes } from "../../../Router/path";
import CustomCard from "../../Shared/Components/CustomCard/CustomCard";
import InfoIcon from '@mui/icons-material/Info';
import { Box } from "@mui/system";
import { Typography } from "@mui/material";

const Hotels = () => {
    const [filters, setFilters] = useState<HotelSearchFilters>({
        name: "",
        published: "true",
    });

    const [page, setPage] = useState(0); // zero-based index
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedHotelIds, setSelectedHotelIds] = useState<number[]>([]);

    const { data, isLoading, isError, error } = useGetHotelsListQuery({
        SearchPhrase: filters.name,
        onlyPublished:
            filters.published === "" ? undefined : filters.published === "true",
        pageNumber: page + 1, // API 1-based
        pageSize: rowsPerPage,
    } as HotelListRequest);

    const [deleteHotel] = useDeleteHotelMutation();
    const navigate = useNavigate();

    const handleCreate = useCreateRedirect(HotelsRoutes.create);

    const handleDeleteSelected = useDeleteSelectedItems<number>(
        selectedHotelIds,
        (id) => deleteHotel(id).unwrap(),
        () => setSelectedHotelIds([])
    );

    const columns: ColumnConfig<Hotel>[] = [
        { key: "id", label: "ID" },
        { key: "name", label: "Nazwa" },
        { key: "city", label: "Miasto" },
        { key: "displayOrder", label: "Kolejność wyświetlania" },
        {
            key: "published",
            label: "Opublikowany",
            render: (row) => (
                <input type="checkbox" disabled checked={row.published} />
            ),
        },
    ];

    return (
        <>
            <CustomCard
                icon={<InfoIcon fontSize="large" color="primary" />}
                title="Hotele"
                subheader="Zarządzaj dostępnymi hotelami w systemie"
                sx={{ mb: 4 }}
            >
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" gutterBottom>
                        Na tej stronie możesz dodać, edytować i publikować hotele.
                        Aby opublikować hotel, najpierw musisz wprowadzić podstawowe informacje
                        i dodać co najmniej jedno zdjęcie.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Skorzystaj z przycisków poniżej, żeby dodać nowy hotel, usunąć zaznaczone lub wybierz hotel z listy,
                        aby go edytować.
                    </Typography>
                </Box>
                <ActionButtons
                    onCreate={handleCreate}
                    onDeleteSelected={handleDeleteSelected}
                />
                <HotelSearchForm onSearch={setFilters} />
            </CustomCard>
            <CustomCard >
                <DataTable
                    columns={columns}
                    data={data?.items || []}
                    isLoading={isLoading}
                    isError={isError}
                    errorMessage={(error as any)?.data?.message}
                    getRowId={(row) => row.id}
                    onEdit={(id) => {
                        const item = data?.items.find(item => item.id === id);
                        if (item) {
                            navigate(HotelsRoutes.update.replace(":hotelId", item.id.toString()));
                        }
                    }}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalCount={data?.totalCount || 0}
                    onPageChange={(newPage) => setPage(newPage)}
                    onRowsPerPageChange={(newSize) => {
                        setRowsPerPage(newSize);
                        setPage(0);
                    }}
                    selected={selectedHotelIds}
                    onSelectionChange={(selectedIds) => {
                        setSelectedHotelIds(selectedIds);
                    }}
                />
            </CustomCard>
        </>
    );
};

export default Hotels;
